import { Timestamp } from "firebase/firestore";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryClient";
import { toDate } from "@/lib/format";
import { OPEN_ORDER_STATUSES, type Order, type OrderStatus } from "@/types";
import { fetchAggregate, orderBy, where } from "./use-firestore";
import { usePagedQuery } from "./use-paged-query";

export const ORDER_RANGES = {
  today: { label: "Hoje", days: 1 },
  "7d": { label: "7 dias", days: 7 },
  "30d": { label: "30 dias", days: 30 },
  "90d": { label: "90 dias", days: 90 },
} as const;

export type OrderRange = keyof typeof ORDER_RANGES;

export type OrderStatusFilter = OrderStatus | "todos" | "abertos";

export interface OrdersSummary {
  total: number;
  openCount: number;
  deliveredCount: number;
  cancelledCount: number;
  revenue: number;
}

function startOf(range: OrderRange): Date {
  const since = new Date();
  since.setHours(0, 0, 0, 0);
  since.setDate(since.getDate() - (ORDER_RANGES[range].days - 1));
  return since;
}

/**
 * Os filtros do período e do estabelecimento, comuns à lista e aos totais.
 *
 * O `orderBy` vai junto mesmo na agregação, onde não muda o resultado: é ele
 * que faz a contagem usar exatamente os índices que a lista já usa
 * (`createdAt`, `sellerId` mais `createdAt`, `status` mais `createdAt` e os três
 * juntos), em vez de pedir índices novos na direção contrária.
 */
function baseConstraints(range: OrderRange, sellerId: string, status: OrderStatusFilter) {
  const constraints = [where("createdAt", ">=", Timestamp.fromDate(startOf(range)))];
  if (sellerId !== "todos") constraints.push(where("sellerId", "==", sellerId));
  if (status === "abertos") constraints.push(where("status", "in", OPEN_ORDER_STATUSES));
  else if (status !== "todos") constraints.push(where("status", "==", status));
  return [...constraints, orderBy("createdAt", "desc")];
}

/**
 * A visão global de pedidos, uma página por vez.
 *
 * Antes a tela trazia o período inteiro para mostrar a tabela, e noventa dias
 * de uma plataforma movimentada são dezenas de milhares de documentos. Agora o
 * período, a situação e o estabelecimento entram na consulta, e cada página é
 * uma consulta com `limit` (ver `usePagedQuery`).
 *
 * `createdAt` é escrito pelo app do cliente em todo pedido, então o `orderBy`
 * aqui é seguro.
 */
export function useOrdersPage(
  range: OrderRange,
  status: OrderStatusFilter,
  sellerId: string,
  total?: number,
) {
  return usePagedQuery<Order>({
    key: queryKeys.ordersFeed(range, status, sellerId),
    path: "orders",
    constraints: baseConstraints(range, sellerId, status),
    total,
  });
}

/**
 * Os totais do período, calculados pelo servidor por agregação.
 *
 * Não dependem do filtro de situação: os cartões respondem "como foi o período
 * para este estabelecimento", e um cartão de "em aberto" zerado porque a lista
 * está filtrada em "entregues" não diz nada.
 */
export function useOrdersSummary(range: OrderRange, sellerId: string) {
  return useQuery({
    queryKey: [...queryKeys.orders(range), "summary", sellerId],
    queryFn: async (): Promise<OrdersSummary> => {
      const [all, open, delivered, cancelled] = await Promise.all([
        fetchAggregate("orders", baseConstraints(range, sellerId, "todos")),
        fetchAggregate("orders", baseConstraints(range, sellerId, "abertos")),
        fetchAggregate("orders", baseConstraints(range, sellerId, "delivered"), "total"),
        fetchAggregate("orders", baseConstraints(range, sellerId, "cancelled")),
      ]);
      return {
        total: all.count,
        openCount: open.count,
        deliveredCount: delivered.count,
        cancelledCount: cancelled.count,
        revenue: delivered.sum,
      };
    },
  });
}

/** Quantos pedidos a lista filtrada tem, para o rodapé dizer "de N". */
export function useOrdersCount(range: OrderRange, status: OrderStatusFilter, sellerId: string) {
  return useQuery({
    queryKey: [...queryKeys.ordersFeed(range, status, sellerId), "count"],
    queryFn: () => fetchAggregate("orders", baseConstraints(range, sellerId, status)),
    select: (result) => result.count,
  });
}

/** Quando o pedido foi entregue, pela entrada do histórico. */
export function deliveredAt(order: Order): Date | null {
  const entry = order.statusHistory?.find((h) => h.status === "delivered");
  return toDate(entry?.timestamp) ?? (order.status === "delivered" ? toDate(order.updatedAt) : null);
}
